import { Button } from '@newcore/ui/buttons';
import { cn } from '@newcore/ui/cn';
import { FontWeights } from '@newcore/ui/enums/font-weights';
import { Sizes } from '@newcore/ui/enums/sizes';
import { TextSizes } from '@newcore/ui/enums/typography-text-sizes';
import { ButtonVariants } from '@newcore/ui/enums/variants';
import { Text } from '@newcore/ui/typography';
import { LogoVertical } from '@/components/logos';
import {
  CONTACT_SUPPORT_LABEL,
  RETRY_LABEL,
  SskFailureAction,
  type SskFailureCopy,
} from '@/utils/ssk-failure';

export interface SskSigningFailure {
  copy: SskFailureCopy;
  supportMailto: string;
  onRetry: () => void;
}

interface SskSigningScreenProps {
  message: string;
  redirecting: boolean;
  error: SskSigningFailure | null;
}

function FailureActions({ copy, supportMailto, onRetry }: SskSigningFailure) {
  const contactSupport = supportMailto ? (
    <Button asChild size={Sizes.MD} variant={ButtonVariants.PRIMARY}>
      <a href={supportMailto}>{CONTACT_SUPPORT_LABEL}</a>
    </Button>
  ) : null;
  const retry = (
    <Button type='button' size={Sizes.MD} onClick={onRetry}>
      {RETRY_LABEL}
    </Button>
  );

  // A contact-first bucket without a configured support email falls back to retry.
  const primaryIsContact =
    copy.primaryAction === SskFailureAction.CONTACT_SUPPORT && contactSupport;

  return (
    <div className='flex flex-col items-center gap-3'>
      {primaryIsContact ? contactSupport : retry}
      {copy.secondary && (
        <Text as='p' size={TextSizes.XS} className='text-secondary'>
          {copy.secondary}
        </Text>
      )}
      {primaryIsContact ? (
        <Button
          type='button'
          size={Sizes.SM}
          variant={ButtonVariants.LINK_SECONDARY}
          onClick={onRetry}
        >
          {RETRY_LABEL}
        </Button>
      ) : (
        contactSupport && (
          <Button
            asChild
            size={Sizes.SM}
            variant={ButtonVariants.LINK_SECONDARY}
          >
            <a href={supportMailto}>{CONTACT_SUPPORT_LABEL}</a>
          </Button>
        )
      )}
    </div>
  );
}

export function SskSigningScreen({
  message,
  redirecting,
  error,
}: SskSigningScreenProps) {
  if (error) {
    return (
      <div className='min-h-screen flex items-center justify-center'>
        <div className='flex flex-col items-center gap-6 text-center max-w-sm'>
          <LogoVertical />
          <Text
            as='h1'
            data-testid='ssk-signing-error'
            size={TextSizes.LG}
            weight={FontWeights.MEDIUM}
            className='text-error-primary'
          >
            {error.copy.headline}
          </Text>
          <Text as='p' size={TextSizes.SM} className='text-secondary'>
            {error.copy.body}
          </Text>
          <FailureActions {...error} />
        </div>
      </div>
    );
  }

  return (
    <div className='min-h-screen flex items-center justify-center'>
      <div className='flex flex-col items-center gap-6'>
        <LogoVertical />
        <div className='w-[200px] h-[3px] bg-secondary rounded-sm overflow-hidden'>
          <div
            className={cn(
              'h-full bg-[var(--color-text-primary)] rounded-sm',
              redirecting ? 'animate-progress-fill' : 'animate-progress-pulse'
            )}
          />
        </div>
        <Text as='p' size={TextSizes.LG} className='text-secondary'>
          {message}
        </Text>
      </div>
    </div>
  );
}
